/**
 * Widget público de citas (bookingMode='appointments').
 *
 * Flujo (un paso por pantalla, con auto-avance al elegir):
 *   1. Selección de servicio
 *   2. (Opcional, si el centro lo permite) Selección de profesional
 *   3. Selección de fecha (día) → auto-avanza al elegir día (cambiar de mes NO avanza)
 *   4. Selección de hora → auto-avanza al elegir hora
 *   5. Datos del cliente y confirmación
 *   (Si no se elige profesional, o el centro no lo permite, el backend auto-asigna)
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PoweredBy from "../components/PoweredBy";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Clock,
  MapPin,
  Phone,
  Users,
} from "lucide-react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import {
  DateStep,
  TimeStep,
  type Slot,
} from "../components/AppointmentDateTimeSteps";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

interface Org {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  logo: string | null;
  clientChoosesSpecialist?: boolean;
}
interface Service {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  durationMinutes: number;
  price: number;
  color: string;
}
interface Specialist {
  id: string;
  firstName: string;
  lastName: string | null;
  color: string;
  avatar: string | null;
  bio: string | null;
}
type Step =
  | "service"
  | "specialist"
  | "date"
  | "time"
  | "details"
  | "success";

export default function AppointmentsApp() {
  const { slug } = useParams<{ slug: string }>();

  const [org, setOrg] = useState<Org | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("service");
  const [serviceId, setServiceId] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [time, setTime] = useState<string>("");

  // "" = "Cualquiera disponible" (auto-asigna el backend, comportamiento actual)
  const [specialistId, setSpecialistId] = useState<string>("");
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [specialistsLoading, setSpecialistsLoading] = useState(false);

  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [calendarDays, setCalendarDays] = useState<
    { date: string; available: boolean }[]
  >([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    countryCode: "+34",
    phone: "",
    email: "",
    notes: "",
    // "A nombre de" (1-oct-2026): quien reserva sigue siendo el cliente de
    // arriba (teléfono/email/nombre, a quien se avisa), pero la cita puede
    // ponerse a nombre de otra persona (p. ej. un padre que saca cita para su
    // hijo sin registrarlo con su teléfono). El nombre solo cuenta si la
    // casilla está marcada.
    bookingForSomeoneElse: false,
    bookingFor: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    code: string;
    date: string;
    time: string;
    specialistName: string | null;
    roomName: string | null;
  } | null>(null);

  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId) ?? null,
    [services, serviceId],
  );

  const selectedSpecialist = useMemo(
    () => specialists.find((s) => s.id === specialistId) ?? null,
    [specialists, specialistId],
  );

  // Solo se muestran las horas donde el profesional elegido está disponible.
  // Si no hay profesional elegido ("Cualquiera disponible"), se muestran todas.
  const visibleSlots = useMemo(() => {
    if (!specialistId) return slots;
    return slots.filter((s) =>
      s.specialists.some((sp) => sp.id === specialistId),
    );
  }, [slots, specialistId]);

  // Carga inicial
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const [orgRes, svRes] = await Promise.all([
          fetch(`${API_URL}/public/appointments/${slug}`).then((r) => r.json()),
          fetch(`${API_URL}/public/appointments/${slug}/services`).then((r) =>
            r.json(),
          ),
        ]);
        if (!orgRes.success) throw new Error(orgRes.message);
        if (!svRes.success) throw new Error(svRes.message);
        setOrg(orgRes.data);
        setServices(svRes.data);
      } catch (err: any) {
        setError(err.message ?? "Error al cargar");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Cargar profesionales al entrar en el paso "specialist" (solo si el centro
  // permite elegir profesional; ver ServiceStep.onSelect)
  useEffect(() => {
    if (step !== "specialist" || !serviceId || !slug) return;
    setSpecialistsLoading(true);
    const url = new URL(`${API_URL}/public/appointments/${slug}/specialists`);
    url.searchParams.set("serviceId", serviceId);
    fetch(url.toString())
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setSpecialists(res.data);
      })
      .finally(() => setSpecialistsLoading(false));
  }, [step, serviceId, slug]);

  // Cargar calendario al entrar en el paso "date" o cambiar de mes.
  // El resultado se cachea en estado y se reutiliza al volver a "date" desde
  // "time" (no se limpia al cambiar de step), así que no hace falta refetch
  // al navegar hacia atrás dentro del mismo mes.
  useEffect(() => {
    if (step !== "date" || !serviceId || !slug) return;
    setCalendarLoading(true);
    const from = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
    const to = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
    const url = new URL(`${API_URL}/public/appointments/${slug}/calendar`);
    url.searchParams.set("from", from);
    url.searchParams.set("to", to);
    url.searchParams.set("serviceId", serviceId);
    if (specialistId) url.searchParams.set("specialistId", specialistId);
    fetch(url.toString())
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setCalendarDays(res.data);
      })
      .finally(() => setCalendarLoading(false));
  }, [step, serviceId, slug, calendarMonth, specialistId]);

  // Cargar slots al elegir día
  useEffect(() => {
    if (!date || !serviceId || !slug) {
      setSlots([]);
      return;
    }
    setSlotsLoading(true);
    const url = new URL(`${API_URL}/public/appointments/${slug}/slots`);
    url.searchParams.set("date", date);
    url.searchParams.set("serviceId", serviceId);
    fetch(url.toString())
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setSlots(res.data);
      })
      .finally(() => setSlotsLoading(false));
  }, [date, serviceId, slug]);

  function handleSelectSlot(slot: Slot) {
    setTime(slot.time);
    setStep("details");
  }

  async function handleSubmit() {
    setFormError(null);
    if (!form.firstName.trim() || !form.phone.trim()) {
      setFormError("Nombre y teléfono son obligatorios");
      return;
    }
    const emailTrim = form.email.trim();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setFormError("Introduce un email válido para recibir la confirmación");
      return;
    }
    // "A nombre de": solo cuenta con la casilla marcada. Marcada y sin nombre =
    // se pide rellenarlo (no se reserva en silencio sin la otra persona).
    // Sin otra persona, la clave se OMITE del body (nunca cadena vacía).
    const bookingName = form.bookingForSomeoneElse
      ? form.bookingFor.trim()
      : "";
    if (form.bookingForSomeoneElse && !bookingName) {
      setFormError(
        "Escribe el nombre de la persona que recibe la cita, o desmarca la casilla",
      );
      return;
    }
    if (!time || !date || !serviceId) {
      setFormError("Faltan datos de la cita");
      return;
    }
    setSubmitting(true);
    try {
      // Sanitizar teléfono: solo dígitos y, si el usuario incluyó el prefijo
      // (ej "+34 612..."), quitarlo para no duplicarlo con countryCode.
      const cc = form.countryCode.replace(/\D/g, "");
      let phoneDigits = form.phone.replace(/\D/g, "");
      if (cc && phoneDigits.startsWith(cc))
        phoneDigits = phoneDigits.slice(cc.length);
      const res = await fetch(`${API_URL}/public/appointments/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serviceId,
          specialistId: specialistId || undefined,
          date,
          startTime: time,
          customer: {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            phone: phoneDigits,
            countryCode: form.countryCode,
            email: form.email.trim(),
          },
          notes: form.notes.trim() || undefined,
          bookingName: bookingName || undefined,
          acceptsMarketing: acceptMarketing,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || "Error al crear la cita");
      }
      setConfirmation({
        code: data.data.confirmationCode,
        date: data.data.date,
        time: data.data.startTime,
        specialistName:
          `${data.data.specialist?.firstName ?? ""} ${data.data.specialist?.lastName ?? ""}`.trim() ||
          null,
        roomName: data.data.appointmentRoom?.name ?? null,
      });
      setStep("success");
    } catch (err: unknown) {
      setFormError(
        err instanceof Error ? err.message : "Error al crear la cita",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-md text-center shadow">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">{error}</h1>
        </div>
      </div>
    );
  }
  if (!org) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-2">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
          {org.logo && (
            <img
              src={org.logo}
              alt={org.name}
              className="w-14 h-14 rounded-xl object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {org.name}
            </h1>
            {org.address && (
              <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {org.address}
              </p>
            )}
          </div>
          {org.phone && (
            <a
              href={`tel:${org.phone}`}
              className="p-2 bg-indigo-50 rounded-lg text-indigo-600 hover:bg-indigo-100"
            >
              <Phone className="w-5 h-5" />
            </a>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-2xl mx-auto px-4 mt-4">
        <Stepper
          step={step}
          showSpecialistStep={org.clientChoosesSpecialist === true}
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {step === "service" && (
          <ServiceStep
            services={services}
            onSelect={(id) => {
              setServiceId(id);
              setSpecialistId("");
              setStep(
                org.clientChoosesSpecialist === true ? "specialist" : "date",
              );
            }}
          />
        )}

        {step === "specialist" && selectedService && (
          <SpecialistStep
            specialists={specialists}
            loading={specialistsLoading}
            selectedId={specialistId}
            serviceName={selectedService.name}
            onSelect={(id) => {
              setSpecialistId(id);
              setStep("date");
            }}
            onBack={() => setStep("service")}
          />
        )}

        {step === "date" && selectedService && (
          <DateStep
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            calendarDays={calendarDays}
            calendarLoading={calendarLoading}
            selectedDate={date}
            onDateSelect={(d) => {
              setDate(d);
              setTime("");
              setStep("time");
            }}
            onBack={() =>
              setStep(
                org.clientChoosesSpecialist === true ? "specialist" : "service",
              )
            }
            serviceName={selectedService.name}
            durationMinutes={selectedService.durationMinutes}
            specialistName={
              selectedSpecialist
                ? `${selectedSpecialist.firstName} ${selectedSpecialist.lastName ?? ""}`.trim()
                : null
            }
          />
        )}

        {step === "time" && selectedService && date && (
          <TimeStep
            date={date}
            slots={visibleSlots}
            slotsLoading={slotsLoading}
            selectedTime={time}
            onSelectSlot={handleSelectSlot}
            onBack={() => setStep("date")}
            onChangeDay={() => setStep("date")}
            serviceName={selectedService.name}
            durationMinutes={selectedService.durationMinutes}
            specialistName={
              selectedSpecialist
                ? `${selectedSpecialist.firstName} ${selectedSpecialist.lastName ?? ""}`.trim()
                : null
            }
          />
        )}

        {step === "details" && selectedService && (
          <DetailsStep
            form={form}
            onChange={(patch) => setForm({ ...form, ...patch })}
            onBack={() => setStep("time")}
            onSubmit={handleSubmit}
            submitting={submitting}
            formError={formError}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            acceptPrivacy={acceptPrivacy}
            setAcceptPrivacy={setAcceptPrivacy}
            acceptMarketing={acceptMarketing}
            setAcceptMarketing={setAcceptMarketing}
            summary={{
              service: selectedService.name,
              date,
              time,
              durationMinutes: selectedService.durationMinutes,
              price: selectedService.price,
              specialistName: selectedSpecialist
                ? `${selectedSpecialist.firstName} ${selectedSpecialist.lastName ?? ""}`.trim()
                : null,
            }}
          />
        )}

        {step === "success" && confirmation && (
          <SuccessStep
            confirmation={confirmation}
            org={org}
            serviceName={selectedService?.name ?? ""}
            onNewAppointment={() => {
              setConfirmation(null);
              setServiceId("");
              setSpecialistId("");
              setSpecialists([]);
              setDate("");
              setTime("");
              setForm({
                firstName: "",
                lastName: "",
                countryCode: "+34",
                phone: "",
                email: "",
                notes: "",
                bookingForSomeoneElse: false,
                bookingFor: "",
              });
              setAcceptTerms(false);
              setAcceptPrivacy(false);
              setAcceptMarketing(false);
              setStep("service");
            }}
          />
        )}

        <PoweredBy
          slug={slug}
          etiqueta="Con la tecnología de"
          className="mt-6 pb-6"
        />
      </div>
    </div>
  );
}

// ---------- Subcomponentes ----------

function Stepper({
  step,
  showSpecialistStep,
}: {
  step: Step;
  showSpecialistStep: boolean;
}) {
  const steps: Step[] = showSpecialistStep
    ? ["service", "specialist", "date", "time", "details"]
    : ["service", "date", "time", "details"];
  const labels: Record<Step, string> = {
    service: "Servicio",
    specialist: "Profesional",
    date: "Fecha",
    time: "Hora",
    details: "Datos",
    success: "Listo",
  };
  const idx = steps.indexOf(step);
  return (
    <div className="flex items-center justify-between text-xs">
      {steps.map((s, i) => {
        const active = i === idx;
        const done = i < idx || step === "success";
        return (
          <div key={s} className="flex items-center flex-1 last:flex-initial">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-[11px] ${
                done
                  ? "bg-indigo-600 text-white"
                  : active
                    ? "bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500"
                    : "bg-gray-200 text-gray-500"
              }`}
            >
              {done ? "✓" : i + 1}
            </div>
            <span
              className={`ml-2 hidden sm:inline ${
                active ? "text-indigo-700 font-medium" : "text-gray-500"
              }`}
            >
              {labels[s]}
            </span>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 ${
                  done ? "bg-indigo-600" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ServiceStep({
  services,
  onSelect,
}: {
  services: Service[];
  onSelect: (id: string) => void;
}) {
  // Agrupar por categoría
  const grouped = services.reduce<Record<string, Service[]>>((acc, sv) => {
    const k = sv.category ?? "Servicios";
    (acc[k] ||= []).push(sv);
    return acc;
  }, {});

  if (services.length === 0) {
    return (
      <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
        No hay servicios disponibles para reservar online.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold text-gray-800">
        ¿Qué servicio quieres reservar?
      </h2>
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2 px-1">
            {cat}
          </h3>
          <div className="space-y-2">
            {list.map((sv) => (
              <button
                key={sv.id}
                onClick={() => onSelect(sv.id)}
                className="w-full bg-white hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-xl p-4 flex items-center gap-3 text-left transition shadow-sm"
              >
                <span
                  className="w-1.5 h-12 rounded-full"
                  style={{ backgroundColor: sv.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{sv.name}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                    <Clock className="w-3.5 h-3.5" />
                    {sv.durationMinutes} min
                    {sv.price > 0 && (
                      <span className="ml-1 font-semibold text-gray-700">
                        · {sv.price.toFixed(2)}€
                      </span>
                    )}
                  </p>
                  {sv.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {sv.description}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SpecialistAvatar({
  specialist,
  size = "w-11 h-11",
}: {
  specialist: Pick<Specialist, "firstName" | "lastName" | "color" | "avatar">;
  size?: string;
}) {
  if (specialist.avatar) {
    return (
      <img
        src={specialist.avatar}
        alt=""
        className={`${size} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${size} rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm`}
      style={{ backgroundColor: specialist.color || "#6366f1" }}
    >
      {specialist.firstName.charAt(0).toUpperCase()}
      {(specialist.lastName ?? "").charAt(0).toUpperCase()}
    </div>
  );
}

function SpecialistStep({
  specialists,
  loading,
  selectedId,
  serviceName,
  onSelect,
  onBack,
}: {
  specialists: Specialist[];
  loading: boolean;
  selectedId: string;
  serviceName: string;
  onSelect: (id: string) => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-white border rounded-xl p-3 text-sm text-gray-600">
        <div className="font-medium text-gray-900">{serviceName}</div>
      </div>

      <h2 className="text-base font-semibold text-gray-800">
        ¿Con quién quieres tu cita?
      </h2>

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={() => onSelect("")}
            className={`w-full bg-white hover:bg-indigo-50 border rounded-xl p-4 flex items-center gap-3 text-left transition shadow-sm ${
              selectedId === ""
                ? "border-indigo-400 ring-1 ring-indigo-300"
                : "border-gray-200 hover:border-indigo-300"
            }`}
          >
            <div className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center bg-indigo-100 text-indigo-600">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">
                Cualquiera disponible
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Te asignamos el primer profesional libre a esa hora
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>

          {specialists.map((sp) => (
            <button
              key={sp.id}
              onClick={() => onSelect(sp.id)}
              className={`w-full bg-white hover:bg-indigo-50 border rounded-xl p-4 flex items-center gap-3 text-left transition shadow-sm ${
                selectedId === sp.id
                  ? "border-indigo-400 ring-1 ring-indigo-300"
                  : "border-gray-200 hover:border-indigo-300"
              }`}
            >
              <SpecialistAvatar specialist={sp} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {sp.firstName} {sp.lastName ?? ""}
                </p>
                {sp.bio && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {sp.bio}
                  </p>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          ))}

          {specialists.length === 0 && (
            <p className="text-xs text-gray-500 text-center py-2">
              No hay profesionales concretos disponibles para este servicio;
              te asignaremos uno automáticamente.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DetailsStep({
  form,
  onChange,
  onBack,
  onSubmit,
  submitting,
  formError,
  summary,
  acceptTerms,
  setAcceptTerms,
  acceptPrivacy,
  setAcceptPrivacy,
  acceptMarketing,
  setAcceptMarketing,
}: {
  form: {
    firstName: string;
    lastName: string;
    countryCode: string;
    phone: string;
    email: string;
    notes: string;
    bookingForSomeoneElse: boolean;
    bookingFor: string;
  };
  onChange: (patch: Partial<typeof form>) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  formError: string | null;
  summary: {
    service: string;
    date: string;
    time: string;
    durationMinutes: number;
    price: number;
    specialistName?: string | null;
  };
  acceptTerms: boolean;
  setAcceptTerms: (v: boolean) => void;
  acceptPrivacy: boolean;
  setAcceptPrivacy: (v: boolean) => void;
  acceptMarketing: boolean;
  setAcceptMarketing: (v: boolean) => void;
}) {
  // NOTA (23-jul-2026, RGPD): aquí había un autocompletado de nombre/apellidos/email
  // al teclear el teléfono, contra un endpoint público SIN autenticación
  // (`/public/appointments/:slug/customer-lookup`) que devolvía los datos de
  // cualquier cliente ya registrado con ese número. Un dictamen de cumplimiento
  // detectó que eso permitía a cualquiera comprobar si un teléfono es cliente de
  // un negocio y obtener su nombre/email sin ninguna autenticación. El endpoint
  // se retiró del backend y esta llamada con él. NO reintroducir sin rediseñar
  // el lookup para que requiera algo más que el teléfono en claro.

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-1 text-sm text-indigo-900">
        <p className="font-semibold">{summary.service}</p>
        <p>
          {format(new Date(summary.date), "EEEE d 'de' MMMM", { locale: es })} ·{" "}
          {summary.time} · {summary.durationMinutes} min
        </p>
        {summary.specialistName && (
          <p className="text-indigo-800">Con: {summary.specialistName}</p>
        )}
        {summary.price > 0 && (
          <p className="font-semibold">Precio: {summary.price.toFixed(2)}€</p>
        )}
      </div>

      <div className="space-y-3">
        <Field label="Teléfono *">
          <div className="flex gap-2 w-full">
            <select
              value={form.countryCode}
              onChange={(e) => onChange({ countryCode: e.target.value })}
              className="w-20 sm:w-24 flex-shrink-0 px-2 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="+34">🇪🇸 +34</option>
              <option value="+33">🇫🇷 +33</option>
              <option value="+49">🇩🇪 +49</option>
              <option value="+44">🇬🇧 +44</option>
              <option value="+39">🇮🇹 +39</option>
              <option value="+351">🇵🇹 +351</option>
            </select>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => onChange({ phone: e.target.value })}
              placeholder="612 345 678"
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </Field>
        <Field label="Nombre *">
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => onChange({ firstName: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>
        <Field label="Apellidos">
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => onChange({ lastName: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>
        <Field label="Email *">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="tu@email.com"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Te enviaremos la confirmación y los enlaces para gestionar tu cita.
          </p>
        </Field>
        {/* "A nombre de" (1-oct-2026): la cita puede ser para otra persona (un
            hijo, un familiar). Quien reserva sigue siendo el de arriba: su
            teléfono y email son a los que avisamos. */}
        <div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.bookingForSomeoneElse}
              onChange={(e) =>
                onChange({ bookingForSomeoneElse: e.target.checked })
              }
              className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              ¿La cita es para otra persona?{" "}
              <span className="text-gray-500">
                (por ejemplo, un hijo o un familiar)
              </span>
            </span>
          </label>
          {form.bookingForSomeoneElse && (
            <div className="mt-3">
              <Field label="Nombre de la persona que recibe la cita *">
                <input
                  type="text"
                  value={form.bookingFor}
                  onChange={(e) => onChange({ bookingFor: e.target.value })}
                  maxLength={120}
                  autoComplete="off"
                  placeholder="Nombre y apellidos"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La cita se pondrá a su nombre. Seguiremos avisándote a ti, en
                  este teléfono y email.
                </p>
              </Field>
            </div>
          )}
        </div>
        <Field label="Notas (opcional)">
          <textarea
            value={form.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>
      </div>

      <div className="space-y-2 pt-2 border-t border-gray-100 text-sm">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-600">
            Acepto los{" "}
            <a
              href="https://reservamanager.com/terminos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              Términos y Condiciones
            </a>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptPrivacy}
            onChange={(e) => setAcceptPrivacy(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-600">
            Acepto la{" "}
            <a
              href="https://reservamanager.com/privacidad-cita"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              Política de Privacidad
            </a>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptMarketing}
            onChange={(e) => setAcceptMarketing(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-500">
            Recibir ofertas y novedades del negocio
          </span>
        </label>
      </div>

      {formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      <button
        disabled={submitting || !acceptTerms || !acceptPrivacy}
        onClick={onSubmit}
        className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium py-3 rounded-xl shadow flex items-center justify-center gap-2"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        Confirmar cita
      </button>
    </div>
  );
}

function SuccessStep({
  confirmation,
  org,
  serviceName,
  onNewAppointment,
}: {
  confirmation: {
    code: string;
    date: string;
    time: string;
    specialistName: string | null;
    roomName: string | null;
  };
  org: Org;
  serviceName: string;
  onNewAppointment: () => void;
}) {
  return (
    <div className="bg-white border rounded-2xl p-8 text-center shadow-sm">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-9 h-9 text-green-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        ¡Cita confirmada!
      </h2>
      <p className="text-gray-600 mb-6">
        Te hemos guardado el hueco de <strong>{serviceName}</strong>.
      </p>
      <div className="bg-gray-50 border rounded-xl p-4 text-left space-y-2 text-sm">
        <p>
          <span className="text-gray-500">Fecha: </span>
          <strong>
            {format(new Date(confirmation.date), "EEEE d 'de' MMMM", {
              locale: es,
            })}
          </strong>
        </p>
        <p>
          <span className="text-gray-500">Hora: </span>
          <strong>{confirmation.time}</strong>
        </p>
        <p>
          <span className="text-gray-500">Lugar: </span>
          <strong>{org.name}</strong>
        </p>
        {confirmation.specialistName && (
          <p>
            <span className="text-gray-500">Profesional: </span>
            <strong>{confirmation.specialistName}</strong>
          </p>
        )}
        {confirmation.roomName && (
          <p>
            <span className="text-gray-500">Sala: </span>
            <strong>{confirmation.roomName}</strong>
          </p>
        )}
        <p>
          <span className="text-gray-500">Código: </span>
          <strong className="font-mono">{confirmation.code}</strong>
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-4 mb-6">
        Guarda este código por si necesitas modificar o cancelar tu cita.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onNewAppointment}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-xl shadow"
        >
          Pedir otra cita
        </button>
        <a
          href={`/a/${confirmation.code}`}
          className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-xl text-center"
        >
          Gestionar mi cita
        </a>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
